import React, {
    FC,
    Fragment,
    MutableRefObject,
    ReactElement,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { CrmLicenceTypes, SelfProvisioning, UsersByExtension as UsersByExtensionLanguage } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { DevicesResponse, LicenceResponseMapped, SessionAPIResponse } from '../../../NonReact/Types/Types'
import { COMMON_DEVICES_PARTITION_NAME, EMPTY_STRING } from '../../../NonReact/constants/Common'
import { DEVICE_FILTER_EXTENSIONS_ONLY, EPT_DEVICE_FIELDS } from '../../../NonReact/constants/DeviceConstants'
import { ReportingAccessLevels } from '../../../NonReact/constants/Permissions'
import { StatusCodes } from '../../../NonReact/constants/StatusCodes'
import { isStringNotEmpty, IsVarEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { FormProvider, useForm, useFormContext, UseFormMethods } from '../../../Vendor/react-hook-form/dist/index.ie11'
import { handleUnknownForAvailables } from '../../AddUser/AddUserFunctions'
import { DEFAULT_CME_ID } from '../../AddUser/Constants'
import { RESTAPIError } from '../../Common/API'
import { getSession } from '../../Common/API/CommonAPI'
import { HelpTagTitles, Tags } from '../../Common/API/Contentful/Constants'
import { getCapabilitiesFromCME, getCMEIDFromTelSys } from '../../Common/API/ItemGetters'
import {
    ALL_VALUE,
    getDevicesFromPartition,
    getPartitionsForTelServer,
    getTelephonyServer,
    getTelServersAsSelectOptions
} from '../../Common/API/ItemsGetters'
import { getTelSysLicences } from '../../Common/API/Licence/LicenceAPI'
import { ComboBox } from '../../Common/Components/ComboBox/ComboBox'
import { HelpTag } from '../../Common/Components/HelpTag/HelpTag'
import { PaginatedList } from '../../Common/Components/PaginatedList/PaginatedList'
import { DeviceSelectItem, SelectOption, SelectOptions } from '../../Common/Components/Selects/Types'
import { ON_CHANGE } from '../../Common/Constants/HookFormEventNames'
import { CommonFormContext, CommonFormContextProvider, FormContextType } from '../../Common/Contexts/CommonFormContext'
import MessageContext, { MessageContextType } from '../../Common/Contexts/MessageContext'
import UserInfoContext, { UserInfoContextType } from '../../Common/Contexts/UserInfoContext'
import { useStateAsync } from '../../Common/Hooks/useStateAsync'
import { UserDefaults } from '../../Common/Types'
import { IndividualTelItem, ReactState, ReactStateAsync, TelSysIntegrationMode } from '../../Common/Types/Types'
import { $error } from '../../Common/Utils/ConsoleUtils'
import { displayErrorMessage, displayInfoMessage, displaySuccessMessage } from '../../Common/Utils/Messages'
import { PARTITION_FILTER_NAME, TELSYS_FILTER_NAME } from '../../InsightsHub/Constants/FieldNames'
import { UserTypes } from '../../SelfProvisioning/Constants/Constants'
import { userFormStateMapper } from '../../SelfProvisioning/Mappers/UserMapper'
import { TelSysTypes } from '../../TelSysConfiguration/Constants/Constants'
import userDefaults from '../Config/UserDefaults.json'
import { UserByExtensionEntry } from '../Entries/UserByExtensionEntry'
import { AddAllUsers } from '../Header/AddAllUsers'
import { LicenceSummary } from '../Header/LicenceSummary'
import { UsersByExtensionForm } from '../Types/Types'
import { ADVANCED_FIELDS, HISTORIC_ONLY, INTEGRATION_MODE } from '../../../NonReact/constants/TelSysConstants'
import { PermissionsUtil } from '../../Common/Utils/PermissionsUtil'
import { AdminBackendPermissionNames } from '../../Common/Constants/Permissions'
import { useFeatureFlag } from '../../Common/Hooks/useFeatureFlag'
import { CRM_FEATURE_FLAG_KEY_FOR_TELSYS } from '../../Common/Constants/FeatureFlags'
import { ProvisionAllCrm } from '../Header/ProvisionAllCrm'
import { ArrayUtils } from '../../../NonReact/utils/ArrayUtils'
import { isBitmaskMatching } from '../../Common/Utils/BitwiseOperations'
import { telSysCapabilityBitFlags } from '../../CMEAddModify/Constants'
import {
    CrmIntegrationLicence,
    SelfProvisioningUser
} from '../../../NonReact/common/Types/SelfProvisioning/SelfProvisioningTypes'
import { AdminUserBody, ApplicationUserResponse, JSONResponse } from '../../../NonReact/common/Types/Types'
import { createUser, getUser, updateUser } from '../../../NonReact/common/API/EntityAPIClient'

type AddUserFn = (
    deviceSelectItem: DeviceSelectItem,
    userType: string,
    crmLicence?: CrmIntegrationLicence
) => Promise<boolean>

export const UsersByExtension: FC = (): ReactElement => {
    const methods: UseFormMethods<UsersByExtensionForm> = useForm({
        mode: ON_CHANGE,
        shouldUnregister: false
    })

    const { handleSubmit }: UseFormMethods<UsersByExtensionForm> = methods

    return (
        <FormProvider {...methods}>
            <CommonFormContextProvider>
                <form
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    onSubmit={handleSubmit((): void => {
                        // Do nothing on purpose
                    })}
                >
                    <UsersByExtensionContent />
                </form>
            </CommonFormContextProvider>
        </FormProvider>
    )
}

export const UsersByExtensionContent: FC = (): ReactElement => {
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle(UsersByExtensionLanguage.NAME)
    language.loadBundle(SelfProvisioning.NAME)
    language.loadBundle(CrmLicenceTypes.NAME)

    const userInfo: UserInfoContextType = useContext(UserInfoContext)
    const { displayError }: MessageContextType = useContext(MessageContext)
    const formContext: Nullable<FormContextType> = useContext(CommonFormContext)

    const { reset, watch, setValue }: UseFormMethods<UsersByExtensionForm> = useFormContext()

    const usersFromForm: SelfProvisioningUser[] = watch('users', [])
    const users: SelfProvisioningUser[] = useMemo(
        (): SelfProvisioningUser[] =>
            usersFromForm.map(
                (user: SelfProvisioningUser): SelfProvisioningUser => ({
                    ...user,
                    userType: language.get(`UsersByExtension_UserType_${user.userType}`)
                })
            ),
        [JSON.stringify(usersFromForm)]
    )

    const [cmeID, setCmeID]: ReactState<string> = useState<string>(DEFAULT_CME_ID)
    const [capabilities, setCapabilities]: ReactState<Definable<number>> = useState<Definable<number>>()
    const [availableExtensions, setAvailableExtensions]: ReactState<DeviceSelectItem[]> = useState<DeviceSelectItem[]>(
        []
    )
    const [extensionErrors, setExtensionErrors, getExtensionErrors]: ReactStateAsync<Definable<string>[]> =
        useStateAsync<Definable<string>[]>([])

    const [availableTelServers, setAvailableTelServers]: ReactState<Definable<SelectOptions<number>>> =
        useState<SelectOptions<number>>()
    const [availablePartitions, setAvailablePartitions]: ReactState<Definable<SelectOptions<number>>> =
        useState<SelectOptions<number>>()

    const [currentTelSys, setCurrentTelSys]: ReactState<Nullable<IndividualTelItem>> =
        useState<Nullable<IndividualTelItem>>()

    const [licenceSummary, setLicenceSummary]: ReactStateAsync<Nullable<LicenceResponseMapped>> =
        useStateAsync<Nullable<LicenceResponseMapped>>(undefined)

    const [isLoadingTelServers, setIsLoadingTelServers]: ReactState<boolean> = useState<boolean>(false)
    const [isLoadingPartitions, setIsLoadingPartitions]: ReactState<boolean> = useState<boolean>(false)

    const [loadingText, setLoadingText]: ReactState<Nullable<string>> = useState<Nullable<string>>()

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()
    const hasLoaded: MutableRefObject<boolean> = useRef(false)
    const isPartitionChangeRequired: MutableRefObject<boolean> = useRef(false)

    const historicOnly: boolean = useMemo((): boolean => currentTelSys?.HistoricOnly ?? false, [currentTelSys])

    const doesCurrentUserHaveUserWriteAccess: boolean = useMemo(
        (): boolean =>
            PermissionsUtil.hasPermissions(
                userInfo,
                PermissionsUtil.builder(AdminBackendPermissionNames.USER).create().update()
            ),
        [userInfo]
    )

    const isAdminProvisioningUser: boolean = useMemo(
        (): boolean => doesCurrentUserHaveUserWriteAccess && currentTelSys?.TelSysType === TelSysTypes.MS_TEAMS,
        [doesCurrentUserHaveUserWriteAccess, currentTelSys]
    )

    const crmIntegrationLicenceType: CrmIntegrationLicence = useMemo(
        (): CrmIntegrationLicence =>
            currentTelSys?.TelSysType === TelSysTypes.MS_TEAMS ||
            currentTelSys?.TelSysType === TelSysTypes.WEBEX_CALLING
                ? CrmIntegrationLicence.UNITE
                : CrmIntegrationLicence.SOLO,
        [currentTelSys]
    )

    const telSysValue: number = watch(
        TELSYS_FILTER_NAME,
        availableTelServers ? availableTelServers[0]?.value : ALL_VALUE
    )
    const partitionValue: number = watch(PARTITION_FILTER_NAME)

    const updateDeviceUsers = (index: number, newUsersValue: string[]): void => {
        const newAvailableExtensions: DeviceSelectItem[] = [...availableExtensions]

        if (newAvailableExtensions.length > index && !!newAvailableExtensions?.[index]?.original?.Users) {
            const deviceResponse: DevicesResponse = newAvailableExtensions[index].original as DevicesResponse

            deviceResponse.updateNumber = (deviceResponse.updateNumber ?? 0) + 1
            deviceResponse.Users = newUsersValue
        }

        if (isMounted.current) {
            setAvailableExtensions(newAvailableExtensions)
        }
    }

    const updateExtensionErrors = useCallback(
        (index: number, newError?: string): void => {
            getExtensionErrors().then((errors: Definable<string>[]): void => {
                const newExtensionErrors: Definable<string>[] = [...errors]

                if (newExtensionErrors.length > index) {
                    newExtensionErrors[index] = newError
                }

                if (isMounted.current) {
                    setExtensionErrors(newExtensionErrors)
                }
            })
        },
        [extensionErrors]
    )

    const updateLicenceSummary = useCallback((): void => {
        if (currentTelSys?.TelSysType === TelSysTypes.MS_TEAMS && currentTelSys?.TelSysID !== ALL_VALUE) {
            getTelSysLicences(currentTelSys?.TelSysID)
                .then((telSysLicences: Nullable<LicenceResponseMapped>): Nullable<LicenceResponseMapped> => {
                    setLicenceSummary(telSysLicences)

                    return telSysLicences
                })
                .catch($error)
        }
    }, [currentTelSys])

    const addUser: AddUserFn = async (
        { original, isAgent, index },
        userType,
        crmLicence = CrmIntegrationLicence.NONE
    ): Promise<boolean> => {
        if ((original?.Users?.length ?? 0) === 0) {
            return getSession()
                .then(async (session: JSONResponse<SessionAPIResponse>): Promise<boolean> => {
                    if (isMounted.current && session?.SessionID) {
                        const idFromTelSys: string = original?.TelSysUsername ?? EMPTY_STRING

                        const user: AdminUserBody = await userFormStateMapper(
                            userDefaults as unknown as UserDefaults,
                            {
                                email: original?.TelSysUsername ?? EMPTY_STRING,
                                name: original?.Description ?? EMPTY_STRING,
                                userType,
                                crmLicence,
                                welcomeEmail: true
                            },
                            cmeID,
                            watch(TELSYS_FILTER_NAME, ALL_VALUE),
                            watch(PARTITION_FILTER_NAME, ALL_VALUE),
                            session?.Nonce,
                            original,
                            isAgent,
                            undefined,
                            idFromTelSys
                        )

                        return createUser(user)
                            .then((): boolean => {
                                if (isMounted.current) {
                                    updateDeviceUsers(index ?? 0, [...(original?.Users ?? []), user.Username])
                                    updateExtensionErrors(index ?? 0, undefined)
                                }

                                return true
                            })
                            .catch((error: RESTAPIError): boolean => {
                                if (isMounted.current) {
                                    updateExtensionErrors(index ?? 0, error.message)

                                    if (error.HTTPStatus === StatusCodes.OK) {
                                        updateDeviceUsers(index ?? 0, [...(original?.Users ?? []), user.Username])

                                        return true
                                    }
                                }

                                return false
                            })
                    }

                    return Promise.reject()
                })
                .catch((e: Error): boolean => {
                    displayError(e)

                    return false
                })
        }

        return Promise.resolve(false)
    }

    const devicesWithoutUsers: DeviceSelectItem[] = useMemo((): DeviceSelectItem[] => {
        if (!availableExtensions) return []
        return availableExtensions.filter(
            (ext: DeviceSelectItem): boolean => (ext?.original?.Users ?? [])?.length === 0
        )
    }, [availableExtensions])

    const addAllUsers: (userType: string) => void = useCallback(
        (userType: string): void => {
            if (availableExtensions && availableExtensions.length > 0) {
                setLoadingText(
                    language.getInterpolated(
                        UsersByExtensionLanguage.AddAllUsers_CreatingUsers,
                        language.getWithMSTCheck(`UsersByExtension_UserType_${userType}`, currentTelSys?.TelSysType)
                    )
                )

                let availableLicencesForUserType: number = Number.MAX_SAFE_INTEGER

                if (userType === UserTypes.PROFESSIONAL) {
                    availableLicencesForUserType =
                        licenceSummary?.AvailableLicences?.Professional ?? Number.MAX_SAFE_INTEGER
                } else if (userType === UserTypes.ESSENTIALS) {
                    availableLicencesForUserType =
                        licenceSummary?.AvailableLicences?.Essentials ?? Number.MAX_SAFE_INTEGER
                } else if (userType === UserTypes.CALL_CENTRE_AGENT) {
                    availableLicencesForUserType =
                        licenceSummary?.AvailableLicences?.CallCentreAgent ?? Number.MAX_SAFE_INTEGER
                }

                if (devicesWithoutUsers.length <= 0) {
                    displayInfoMessage(language.get(UsersByExtensionLanguage.NoUsersAdded))
                    setLoadingText(undefined)

                    return undefined
                }

                if (devicesWithoutUsers.length > availableLicencesForUserType && availableLicencesForUserType >= 0) {
                    let tooManyUsersMessage: string

                    if (availableLicencesForUserType <= 0) {
                        tooManyUsersMessage = language.getInterpolated(
                            devicesWithoutUsers.length > 1
                                ? UsersByExtensionLanguage.TooManyUsers_NoLicences_Plural
                                : UsersByExtensionLanguage.TooManyUsers_NoLicences,
                            devicesWithoutUsers.length
                        )
                    } else if (availableLicencesForUserType === 1) {
                        tooManyUsersMessage = language.getInterpolated(
                            devicesWithoutUsers.length > 1
                                ? UsersByExtensionLanguage.TooManyUsers_UsersPlural_LicenceNonPlural
                                : UsersByExtensionLanguage.TooManyUsers_UsersNonPlural_LicenceNonPlural,
                            devicesWithoutUsers.length,
                            availableLicencesForUserType
                        )
                    } else {
                        tooManyUsersMessage = language.getInterpolated(
                            devicesWithoutUsers.length > 1
                                ? UsersByExtensionLanguage.TooManyUsers_Plural
                                : UsersByExtensionLanguage.TooManyUsers,
                            devicesWithoutUsers.length,
                            availableLicencesForUserType
                        )
                    }

                    displayErrorMessage(tooManyUsersMessage)
                    setLoadingText(undefined)

                    return undefined
                }

                const totalNumberOfUsers: number = Object.values(licenceSummary?.UsedLicences ?? {}).reduce<number>(
                    (total: number, value: Nullable<number>): number => total + (value ?? 0),
                    0
                )

                if (
                    devicesWithoutUsers.length + totalNumberOfUsers >
                    (currentTelSys?.Advanced?.MaximumAllowedUsersMonitored ?? Number.MAX_SAFE_INTEGER)
                ) {
                    displayErrorMessage(
                        language.getInterpolated(
                            devicesWithoutUsers.length > 1
                                ? UsersByExtensionLanguage.TooManyUsers_MaxTelSysCapReached_Plural
                                : UsersByExtensionLanguage.TooManyUsers_MaxTelSysCapReached,
                            devicesWithoutUsers.length
                        )
                    )
                    setLoadingText(undefined)

                    return undefined
                }

                Promise.all(
                    devicesWithoutUsers.map((device: DeviceSelectItem): Promise<boolean> => addUser(device, userType))
                )
                    .then((results: boolean[]): void => {
                        if (isMounted.current) {
                            const successfullyAddedUsers: number =
                                results?.filter((res: boolean): boolean => res)?.length ?? 0

                            if (successfullyAddedUsers > 0) {
                                displaySuccessMessage(
                                    language.getInterpolated(
                                        UsersByExtensionLanguage.SuccessfullyAddedUsers,
                                        successfullyAddedUsers,
                                        language.getWithMSTCheck(
                                            `UsersByExtension_UserType_${userType}`,
                                            currentTelSys?.TelSysType
                                        )
                                    )
                                )
                            } else {
                                displayInfoMessage(language.get(UsersByExtensionLanguage.NoUsersAdded))
                            }
                        }
                    })
                    .catch(displayError)
                    .finally((): void => {
                        setLoadingText(undefined)
                        updateLicenceSummary()
                    })
            }

            return undefined
        },
        [availableExtensions, licenceSummary, currentTelSys]
    )

    const isCXIFeatureEnabled: boolean =
        useFeatureFlag(CRM_FEATURE_FLAG_KEY_FOR_TELSYS[currentTelSys?.TelSysType as TelSysTypes], false) ?? false

    const isCXICapabilityEnabled: boolean =
        isVarNotEmpty(currentTelSys?.TelSysType) &&
        isVarNotEmpty(capabilities) &&
        isBitmaskMatching(capabilities, telSysCapabilityBitFlags[currentTelSys.TelSysType])

    const isCXIEnabled: boolean = isCXIFeatureEnabled && isCXICapabilityEnabled

    const shouldShowReportingTypeColumn = (): boolean => {
        if (!isCXIEnabled || IsVarEmpty(currentTelSys?.IntegrationMode)) return true
        return currentTelSys?.IntegrationMode !== TelSysIntegrationMode.CXI_ONLY
    }

    const isReportingTypeColumnVisible: boolean = shouldShowReportingTypeColumn()

    const assignUsersCrmLicense = async ({ original, index }: DeviceSelectItem): Promise<boolean> => {
        const username: string = original?.Users?.[0] as string
        return getUser(username)
            .then((user: JSONResponse<ApplicationUserResponse>) => {
                if (user?.Data.Scope.CRMLicenseType.toUpperCase() === crmIntegrationLicenceType) {
                    return false
                }

                return updateUser(username, {
                    Scope: { CRMLicenseType: crmIntegrationLicenceType }
                } as AdminUserBody)
                    .then((): boolean => {
                        const idx: number = index ?? 0

                        if (isMounted.current) {
                            updateDeviceUsers(idx, [...(original?.Users ?? [])])
                            updateExtensionErrors(idx)
                        }

                        return true
                    })
                    .catch((error: RESTAPIError): boolean => {
                        if (isMounted.current) {
                            updateExtensionErrors(index ?? 0, error.message)

                            if (error.HTTPStatus === StatusCodes.OK) {
                                updateDeviceUsers(index ?? 0, [...(original?.Users ?? []), username])

                                return true
                            }
                        }

                        return false
                    })
            })
            .catch((error: RESTAPIError): boolean => {
                if (isMounted.current) {
                    updateExtensionErrors(index ?? 0, error.message)
                }
                return false
            })
    }

    const provisionAllUnlicensedDevicesWithCrmIntegration: () => Promise<void> =
        useCallback(async (): Promise<void> => {
            const unmonitoredDevices: DeviceSelectItem[] = devicesWithoutUsers?.filter(
                ({ isAgent }): boolean => !isAgent
            )

            const existingUsers: DeviceSelectItem[] = availableExtensions.filter(
                (ext: DeviceSelectItem): boolean => (ext?.original?.Users ?? [])?.length > 0
            )

            if (ArrayUtils.isEmpty(unmonitoredDevices) && ArrayUtils.isEmpty(existingUsers)) {
                displayInfoMessage(language.get(UsersByExtensionLanguage.NoUsersAdded))
                return
            }

            setLoadingText(
                language.getInterpolated(
                    UsersByExtensionLanguage.AddAllUsers_CreatingUsers,
                    language.getWithMSTCheck(`CrmLicenceType_${crmIntegrationLicenceType}`, currentTelSys?.TelSysType)
                )
            )

            const requests: Promise<boolean>[] = [
                ...unmonitoredDevices.map(
                    (device: DeviceSelectItem): Promise<boolean> =>
                        addUser(device, UserTypes.NONE, crmIntegrationLicenceType)
                ),
                ...existingUsers.map((device: DeviceSelectItem): Promise<boolean> => assignUsersCrmLicense(device))
            ]

            Promise.all(requests)
                .then((results: boolean[]): void => {
                    const successCount: number = results.filter((success: boolean): boolean => success === true)?.length
                    if (successCount === 0) {
                        displayInfoMessage(language.get(UsersByExtensionLanguage.NoUsersAdded))
                        return
                    }

                    displaySuccessMessage(
                        language.getInterpolated(
                            UsersByExtensionLanguage.SuccessfullyAddedUsers,
                            successCount,
                            language.getWithMSTCheck(
                                `CrmLicenceType_${crmIntegrationLicenceType}`,
                                currentTelSys?.TelSysType
                            )
                        )
                    )
                })
                .catch((err): void => displayError(err as Error))
                .finally((): void => {
                    if (isMounted.current) {
                        setLoadingText(undefined)
                    }
                })
        }, [devicesWithoutUsers])

    useEffect((): void => {
        if (userInfo && !hasLoaded.current) {
            hasLoaded.current = true
            setIsLoadingTelServers(true)
            setValue(PARTITION_FILTER_NAME, undefined)
            getTelServersAsSelectOptions(
                userInfo?.Scope?.CMEnvironmentID ?? DEFAULT_CME_ID,
                true,
                true,
                ReportingAccessLevels.ENTERPRISE
            )
                .then((telServers: SelectOptions<number>): void => {
                    if (isMounted.current) {
                        setIsLoadingTelServers(false)
                        setAvailablePartitions(undefined)
                        setValue(PARTITION_FILTER_NAME, undefined)

                        if (
                            userInfo?.Scope?.TelSysID &&
                            userInfo.Scope.TelSysID.length > 0 &&
                            userInfo.Scope.TelSysID[0] === ALL_VALUE &&
                            telServers.length > 0
                        ) {
                            setValue(TELSYS_FILTER_NAME, telServers[0].value)
                        } else {
                            setValue(TELSYS_FILTER_NAME, userInfo.Scope.TelSysID[0])
                        }

                        handleUnknownForAvailables(telServers, setAvailableTelServers, telSysValue)
                    }
                })
                .catch(displayError)
        }
    }, [userInfo])

    useEffect((): void => {
        if (telSysValue && telSysValue !== ALL_VALUE) {
            isPartitionChangeRequired.current = true
            setIsLoadingPartitions(true)

            if (formContext?.resetDirtyFields) {
                formContext?.resetDirtyFields()
            }

            const telSysFields: string[] = [
                HISTORIC_ONLY,
                INTEGRATION_MODE,
                ADVANCED_FIELDS.MAXIMUM_ALLOWED_USERS_MONITORED
            ]
            getTelephonyServer(telSysValue, ...telSysFields)
                .then(setCurrentTelSys)
                .catch(displayError)

            getCMEIDFromTelSys(telSysValue)
                .then((cme: Definable<string>): void => {
                    if (IsVarEmpty(cme)) {
                        if (isMounted.current) {
                            setCmeID(cme ?? DEFAULT_CME_ID)
                        }
                        return
                    }

                    getCapabilitiesFromCME(cme)
                        .then((capabiltiesFromCME: Definable<number>): void => {
                            if (isMounted.current) {
                                setCapabilities(capabiltiesFromCME)
                                setCmeID(cme)
                            }
                        })
                        .catch(displayError)
                })
                .catch(displayError)

            getPartitionsForTelServer(telSysValue)
                .then(
                    (partitions: SelectOptions<number>): SelectOptions<number> =>
                        partitions.filter(
                            (partition: SelectOption<number>): boolean =>
                                partition.label !== COMMON_DEVICES_PARTITION_NAME
                        )
                )
                .then((partitions: SelectOptions<number>): void => {
                    if (isMounted.current) {
                        setIsLoadingPartitions(false)
                        setValue(PARTITION_FILTER_NAME, undefined)

                        if (partitions.length > 0) {
                            setValue(PARTITION_FILTER_NAME, partitions.find((): boolean => true)?.value ?? 0)
                            isPartitionChangeRequired.current = false

                            handleUnknownForAvailables(partitions, setAvailablePartitions, partitionValue)
                        }
                    }
                })
                .catch(displayError)
        }
    }, [telSysValue])

    useEffect((): void => {
        if (currentTelSys?.TelSysID && partitionValue && !isPartitionChangeRequired.current) {
            setLoadingText(language.get(UsersByExtensionLanguage.AddAllUsers_Loading))

            getDevicesFromPartition(
                currentTelSys?.TelSysID,
                partitionValue,
                false,
                DEVICE_FILTER_EXTENSIONS_ONLY,
                undefined,
                EPT_DEVICE_FIELDS
            )
                .then((devices: DeviceSelectItem[]): void => {
                    setAvailableExtensions(devices)
                    setExtensionErrors(devices.map((): undefined => undefined))
                    setLoadingText(undefined)

                    if (isMounted.current) {
                        reset({
                            telsys: currentTelSys?.TelSysID,
                            partition: partitionValue,
                            users: devices.map(({ isAgent, original }: DeviceSelectItem): SelfProvisioningUser => {
                                const isTeams: boolean = currentTelSys?.TelSysType === TelSysTypes.MS_TEAMS

                                let userType: string = UserTypes.NONE

                                if (!isTeams) {
                                    userType = isAgent ? UserTypes.CALL_CENTRE_AGENT : UserTypes.ESSENTIALS
                                }

                                return {
                                    email: isTeams ? original?.TelSysUsername ?? EMPTY_STRING : EMPTY_STRING,
                                    userType,
                                    welcomeEmail: userDefaults.default.welcomeEmail,
                                    crmLicence: userDefaults.default.crmLicence ?? CrmIntegrationLicence.NONE,
                                    useEmailAsUsername: false
                                }
                            })
                        })
                    }
                })
                .catch((e: Error): void => {
                    displayError(e)
                    setLoadingText(undefined)
                })

            updateLicenceSummary()
        }
    }, [userInfo, currentTelSys, partitionValue, isPartitionChangeRequired.current])

    return (
        <div className='users-by-extension'>
            <PaginatedList
                entries={availableExtensions}
                entriesPerPage={10}
                hasForm
                errors={extensionErrors}
                isLoading={isStringNotEmpty(loadingText)}
                loadingTitle={loadingText}
                updateDependencies={[licenceSummary]}
                objectSearchMutator={(
                    { label, value, original: originalDevice }: DeviceSelectItem,
                    index?: number
                ): string =>
                    [
                        label,
                        value,
                        ...(originalDevice?.Users ?? []),
                        ...([originalDevice?.TelSysUsername] ?? []),
                        ...(isVarNotEmpty(index) && isStringNotEmpty(users?.[index]?.email)
                            ? [users[index].email]
                            : []),
                        ...(isVarNotEmpty(index) && isStringNotEmpty(users?.[index]?.userType)
                            ? [users[index].userType]
                            : [])
                    ].join(' ')
                }
                entryDisplayAsyncMapper={(extension: DeviceSelectItem, index: number): Promise<ReactElement> =>
                    getExtensionErrors().then(
                        (errors: Definable<string>[]): ReactElement => (
                            <UserByExtensionEntry
                                key={`${JSON.stringify(extension)}-${index}`}
                                extension={extension}
                                cmeID={cmeID}
                                telSys={telSysValue}
                                historicOnly={historicOnly}
                                partition={partitionValue}
                                updateDeviceUsers={updateDeviceUsers}
                                updateExtensionErrors={updateExtensionErrors}
                                extensionError={errors[index]}
                                telsysType={currentTelSys?.TelSysType ?? TelSysTypes.UNKNOWN}
                                licenceSummary={licenceSummary}
                                updateLicenceSummary={updateLicenceSummary}
                                isAdminProvisioningUser={isAdminProvisioningUser}
                                shouldShowReportingTypeColumn={isReportingTypeColumnVisible}
                                shouldShowCXIntegrationColumn={isCXIEnabled}
                            />
                        )
                    )
                }
                entryHeaders={[
                    { header: language.get(UsersByExtensionLanguage.UsersByExtensions_Header_Users) },
                    ...(currentTelSys?.TelSysType === TelSysTypes.MS_TEAMS &&
                    availableExtensions.some((device: DeviceSelectItem): boolean =>
                        isVarNotEmpty(device.original?.LicenceType)
                    )
                        ? [
                              {
                                  header: language.get(UsersByExtensionLanguage.UsersByExtensions_Header_MS_Licences),
                                  classes: 'ms-licence'
                              }
                          ]
                        : []),
                    {
                        header: (
                            <Fragment key='email-address-header'>
                                {language.get(UsersByExtensionLanguage.UsersByExtensions_UserFields_Email)}
                                <HelpTag
                                    tag={Tags.EASY_PROVISIONING}
                                    helpContentTitle={HelpTagTitles.EASY_PROVISIONING_EMAIL}
                                />
                            </Fragment>
                        )
                    },
                    ...(isReportingTypeColumnVisible
                        ? [
                              {
                                  header: (
                                      <Fragment key='subscription-type-header'>
                                          {language.get(UsersByExtensionLanguage.UsersByExtensions_UserFields_Type)}
                                          <HelpTag
                                              tag={Tags.EASY_PROVISIONING}
                                              helpContentTitle={HelpTagTitles.EASY_PROVISIONING_SUB_TYPE}
                                          />
                                      </Fragment>
                                  )
                              }
                          ]
                        : []),
                    ...(isCXIEnabled
                        ? [
                              {
                                  header: (
                                      <Fragment key='crm-integration-header'>
                                          {language.get(
                                              UsersByExtensionLanguage.UsersByExtensions_UserFields_CRM_Integration
                                          )}
                                          <HelpTag
                                              tag={Tags.CRM_INTEGRATION}
                                              helpContentTitle={HelpTagTitles.CRM_INTEGRATION}
                                          />
                                      </Fragment>
                                  ),
                                  classes:
                                      currentTelSys?.TelSysType === TelSysTypes.MS_TEAMS ||
                                      currentTelSys?.TelSysType === TelSysTypes.WEBEX_CALLING
                                          ? 'crm-integration-licence-single-option'
                                          : ''
                              }
                          ]
                        : [])
                ]}
                subHeaderContent={
                    <>
                        {currentTelSys?.TelSysType === TelSysTypes.MS_TEAMS ? (
                            <>
                                {isReportingTypeColumnVisible && (
                                    <AddAllUsers
                                        historicOnly={historicOnly}
                                        addAllUsers={addAllUsers}
                                        telSysType={currentTelSys?.TelSysType}
                                        isAdminProvisioningUser={isAdminProvisioningUser}
                                        availableExtensions={availableExtensions.length}
                                    />
                                )}
                                <LicenceSummary
                                    licenceSummary={licenceSummary}
                                    telSysType={currentTelSys?.TelSysType}
                                    shouldIncludeCXI={isCXIEnabled}
                                />
                            </>
                        ) : (
                            <></>
                        )}
                        {isCXIEnabled && (
                            <ProvisionAllCrm
                                crmIntegrationLicenseType={crmIntegrationLicenceType}
                                telSysType={currentTelSys?.TelSysType}
                                provisionAllUsers={provisionAllUnlicensedDevicesWithCrmIntegration}
                            />
                        )}
                    </>
                }
                headerContent={
                    availableTelServers && availableTelServers.length > 0 ? (
                        <div className='ube-filters'>
                            <div className='ubef-filter-title'>
                                {language.get(UsersByExtensionLanguage.UsersByExtensions_Filters)}
                            </div>
                            <div className='ubef-filter-options'>
                                <ComboBox
                                    label=''
                                    name={TELSYS_FILTER_NAME}
                                    defaultOption={availableTelServers[0]}
                                    options={availableTelServers.slice(1)}
                                    disabled={
                                        isLoadingTelServers ||
                                        (userInfo?.Scope?.TelSysID?.length === 1 &&
                                            userInfo?.Scope?.TelSysID[0] !== ALL_VALUE)
                                    }
                                    onChange={(): void => setValue(PARTITION_FILTER_NAME, undefined)}
                                />
                                {availablePartitions && availablePartitions.length > 0 ? (
                                    <ComboBox
                                        label=''
                                        name={PARTITION_FILTER_NAME}
                                        defaultOption={availablePartitions[0]}
                                        options={availablePartitions.slice(1)}
                                        disabled={
                                            isLoadingPartitions ||
                                            (userInfo?.Scope?.PartIDInTelSys?.length === 1 &&
                                                userInfo?.Scope?.PartIDInTelSys[0] !== ALL_VALUE)
                                        }
                                    />
                                ) : (
                                    <></>
                                )}
                            </div>
                        </div>
                    ) : undefined
                }
            />
        </div>
    )
}
